// Цены для витрин: только назначенные. «… ₽» покупатель не видит.
import type { CaseView, Catalog, Direction } from "../api/types";

const RUBLES = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

export function formatRub(amount: number): string {
  return `${RUBLES.format(amount)} ₽`;
}

// Печать только серая: Иван, 24.10.2026, «на запуск только серая».
const PRINT_PRODUCTS = new Set(["print_grey"]);

// Самая низкая цена печати направления: «от 1 690 ₽». null — цен ещё нет.
export function lowestPrintPrice(catalog: Catalog, direction: Direction): number | null {
  const prices = catalog.directions[direction]?.prices ?? [];
  const known = prices
    .filter((price) => PRINT_PRODUCTS.has(price.product) && price.price_rub !== null)
    .map((price) => price.price_rub as number);
  return known.length === 0 ? null : Math.min(...known);
}

export function priceOf(
  catalog: Catalog,
  direction: Direction,
  product: string,
  sizeId: string,
): number | null {
  const price = catalog.directions[direction]?.prices.find(
    (candidate) => candidate.product === product && candidate.size_id === sizeId,
  );
  return price?.price_rub ?? null;
}

export type PricedCase = CaseView & { price_rub: number };

// Виды кейса направления с ценой, в порядке с сервера. Вид без цены не продаётся и не показывается.
export function pricedCases(catalog: Catalog | undefined, direction: Direction): PricedCase[] {
  const cases = catalog?.directions[direction]?.cases ?? [];
  return cases.filter((caseView): caseView is PricedCase => caseView.price_rub !== null);
}

// Строки цены кейса на витрине: виды с одной ценой — одной строкой, «кристалл, портал» и цена.
export function casePriceLines(cases: PricedCase[]): { titles: string; price: number }[] {
  const lines: { titles: string[]; price: number }[] = [];
  for (const caseView of cases) {
    const line = lines.find((candidate) => candidate.price === caseView.price_rub);
    if (line === undefined) lines.push({ titles: [caseView.title_ru.toLowerCase()], price: caseView.price_rub });
    else line.titles.push(caseView.title_ru.toLowerCase());
  }
  return lines.map((line) => ({ titles: line.titles.join(", "), price: line.price }));
}

// Самая низкая назначенная цена продукта направления по всем размерам. null — цен ещё нет.
export function lowestProductPrice(catalog: Catalog, direction: Direction, product: string): number | null {
  const known = (catalog.directions[direction]?.prices ?? [])
    .filter((price) => price.product === product && price.price_rub !== null)
    .map((price) => price.price_rub as number);
  return known.length === 0 ? null : Math.min(...known);
}

// Вилка цен направления для разметки товара (изменение seo): файл модели и серая печать,
// без кейса и без цветной. null — назначенных цен нет.
const SOLD_PRODUCTS = new Set(["file", ...PRINT_PRODUCTS]);

export function directionPriceRange(catalog: Catalog, direction: Direction): { low: number; high: number } | null {
  const known = (catalog.directions[direction]?.prices ?? [])
    .filter((price) => SOLD_PRODUCTS.has(price.product) && price.price_rub !== null)
    .map((price) => price.price_rub as number);
  if (known.length === 0) return null;
  return { low: Math.min(...known), high: Math.max(...known) };
}
